import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Campaign as CampaignIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useCompany } from '../context/CompanyContext';

const CampaignManagement = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'discount',
    discountType: 'percentage',
    discountValue: 0,
    minPurchaseAmount: 0,
    maxDiscountAmount: 0,
    startDate: '',
    endDate: '',
    isActive: true,
    targetCustomers: 'all',
    targetProducts: 'all',
    targetCategories: [],
    usageLimit: 0,
    usedCount: 0,
    conditions: []
  });

  useEffect(() => {
    // TODO: Load campaigns from API
    setCampaigns([
      {
        id: 1,
        name: 'Yaz Sezonu İndirimi',
        description: 'Tüm yaz ürünlerinde %20 indirim',
        type: 'discount',
        discountType: 'percentage',
        discountValue: 20,
        minPurchaseAmount: 100,
        maxDiscountAmount: 500,
        startDate: '2024-06-01',
        endDate: '2024-08-31',
        isActive: true,
        targetCustomers: 'all',
        targetProducts: 'category',
        targetCategories: ['Yaz Ürünleri'],
        usageLimit: 1000,
        usedCount: 250,
        conditions: ['Minimum 100₺ alışveriş']
      },
      {
        id: 2,
        name: 'Yeni Müşteri Kampanyası',
        description: 'İlk alışverişte %15 indirim',
        type: 'discount',
        discountType: 'percentage',
        discountValue: 15,
        minPurchaseAmount: 50,
        maxDiscountAmount: 200,
        startDate: '2024-01-01',
        endDate: '2024-12-31',
        isActive: true,
        targetCustomers: 'new',
        targetProducts: 'all',
        targetCategories: [],
        usageLimit: 500,
        usedCount: 120,
        conditions: ['Sadece yeni müşteriler']
      }
    ]);
  }, []);

  const handleAddCampaign = () => {
    setEditingCampaign(null);
    setFormData({
      name: '',
      description: '',
      type: 'discount',
      discountType: 'percentage',
      discountValue: 0,
      minPurchaseAmount: 0,
      maxDiscountAmount: 0,
      startDate: '',
      endDate: '',
      isActive: true,
      targetCustomers: 'all',
      targetProducts: 'all',
      targetCategories: [],
      usageLimit: 0,
      usedCount: 0,
      conditions: []
    });
    setOpenDialog(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setFormData(campaign);
    setOpenDialog(true);
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    console.log('Save campaign:', formData);
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateUsagePercentage = (campaign) => {
    if (campaign.usageLimit === 0) return 0;
    return (campaign.usedCount / campaign.usageLimit) * 100;
  };

  const getCampaignStatus = (campaign) => {
    const now = new Date();
    const startDate = new Date(campaign.startDate);
    const endDate = new Date(campaign.endDate);

    if (!campaign.isActive) return 'Pasif';
    if (now < startDate) return 'Bekliyor';
    if (now > endDate) return 'Süresi Dolmuş';
    if (campaign.usedCount >= campaign.usageLimit) return 'Limit Dolmuş';
    return 'Aktif';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Aktif': return 'success';
      case 'Bekliyor': return 'warning';
      case 'Süresi Dolmuş': return 'error';
      case 'Limit Dolmuş': return 'error';
      case 'Pasif': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Kampanya Yönetimi
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCampaign}
        >
          Yeni Kampanya
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Campaigns List */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Kampanyalar
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Kampanya Adı</TableCell>
                      <TableCell>Tür</TableCell>
                      <TableCell>İndirim</TableCell>
                      <TableCell>Tarih Aralığı</TableCell>
                      <TableCell>Kullanım</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body1">{campaign.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {campaign.description}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={campaign.type === 'discount' ? 'İndirim' : 'Kampanya'}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {campaign.discountType === 'percentage' ? '%' : '₺'}{campaign.discountValue}
                          </Typography>
                          {campaign.minPurchaseAmount > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Min: ₺{campaign.minPurchaseAmount}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="caption">
                                {campaign.usedCount}/{campaign.usageLimit}
                              </Typography>
                              <Typography variant="caption">
                                {Math.round(calculateUsagePercentage(campaign))}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={calculateUsagePercentage(campaign)}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getCampaignStatus(campaign)}
                            color={getStatusColor(getCampaignStatus(campaign))}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleEditCampaign(campaign)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Kampanya İstatistikleri
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {campaigns.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Kampanya
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="success.main">
                      {campaigns.filter(c => getCampaignStatus(c) === 'Aktif').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Aktif Kampanya
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="warning.main">
                      {campaigns.reduce((sum, c) => sum + c.usedCount, 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam Kullanım
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="info.main">
                      ₺{campaigns.reduce((sum, c) => sum + (c.usedCount * c.discountValue), 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Toplam İndirim
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hızlı İşlemler
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<TrendingUpIcon />}
                  >
                    Performans Raporu
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ScheduleIcon />}
                  >
                    Zamanlanmış Kampanyalar
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<GroupIcon />}
                  >
                    Müşteri Segmentleri
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MoneyIcon />}
                  >
                    Bütçe Yönetimi
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit Campaign Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kampanya Adı"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Açıklama"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Kampanya Türü</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  label="Kampanya Türü"
                  onChange={handleInputChange}
                >
                  <MenuItem value="discount">İndirim</MenuItem>
                  <MenuItem value="gift">Hediye</MenuItem>
                  <MenuItem value="free_shipping">Ücretsiz Kargo</MenuItem>
                  <MenuItem value="buy_x_get_y">X Al Y Kazan</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>İndirim Türü</InputLabel>
                <Select
                  name="discountType"
                  value={formData.discountType}
                  label="İndirim Türü"
                  onChange={handleInputChange}
                >
                  <MenuItem value="percentage">Yüzde (%)</MenuItem>
                  <MenuItem value="fixed">Sabit Tutar (₺)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="İndirim Değeri"
                name="discountValue"
                type="number"
                value={formData.discountValue}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Minimum Alış Tutarı"
                name="minPurchaseAmount"
                type="number"
                value={formData.minPurchaseAmount}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Maksimum İndirim Tutarı"
                name="maxDiscountAmount"
                type="number"
                value={formData.maxDiscountAmount}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Başlangıç Tarihi"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bitiş Tarihi"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Hedef Müşteriler</InputLabel>
                <Select
                  name="targetCustomers"
                  value={formData.targetCustomers}
                  label="Hedef Müşteriler"
                  onChange={handleInputChange}
                >
                  <MenuItem value="all">Tüm Müşteriler</MenuItem>
                  <MenuItem value="new">Yeni Müşteriler</MenuItem>
                  <MenuItem value="vip">VIP Müşteriler</MenuItem>
                  <MenuItem value="loyal">Sadık Müşteriler</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Hedef Ürünler</InputLabel>
                <Select
                  name="targetProducts"
                  value={formData.targetProducts}
                  label="Hedef Ürünler"
                  onChange={handleInputChange}
                >
                  <MenuItem value="all">Tüm Ürünler</MenuItem>
                  <MenuItem value="category">Kategori Bazlı</MenuItem>
                  <MenuItem value="brand">Marka Bazlı</MenuItem>
                  <MenuItem value="specific">Belirli Ürünler</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kullanım Limiti"
                name="usageLimit"
                type="number"
                value={formData.usageLimit}
                onChange={handleInputChange}
                helperText="0 = Sınırsız"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Aktif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>İptal</Button>
          <Button onClick={handleSave} variant="contained">
            {editingCampaign ? 'Güncelle' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignManagement;
